import { Injectable } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { ConfigResource } from '@df/catalog/config/config.resource';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { Client } from '@df/core/service/client';
import { LocalPage } from '../../catalog/ui/page/component/page.decorator';
import { CMS_CONFIG_ZENDESK, IICmsConfigZendeskData } from '../../cms/config/zendesk.cms-config';

export namespace NZendesk {
    export const configUrl = 'marker-io';

    export interface IZendesk {
        hide?: () => void;
        show?: () => void;
    }

    export const THEME = '#202945';
}

declare global {
    interface Window {
        zE?: NZendesk.IZendesk;
        zESettings?: any;
    }
}

@Injectable({
    providedIn: 'root'
})
export class ZendeskService {
    private zendeskVisible = false;

    constructor(
        private requestService: RequestService,
        private configResource: ConfigResource,
        private meta: Meta,
        private client: Client,
        private app: App
    ) {}

    async load(): Promise<void> {
        if (!this.app.isPlatformBrowser) {
            return;
        }
        return this.configResource.loadModelByUrl(CMS_CONFIG_ZENDESK).then(() => {
            const config = this.configResource.getModelByUrl(CMS_CONFIG_ZENDESK) as IICmsConfigZendeskData;
            const id = config?.data?.content?.zendeskId;

            if (id) {
                this.update(id);
                this.meta.subject.subscribe(() => this.update(id));
                this.client.isCms$.subscribe(isCms => {
                    if (isCms && window.zE?.hide) {
                        window.zE.hide();
                        this.zendeskVisible = false;
                    } else {
                        this.update(id);
                    }
                });
            }
        });
    }

    private update(id: string): void {
        if (!this.client.isCms) {
            const model = this.getPageMetaModel();

            const hide = (model?.data as any)?.additional?.hideZendesk;
            if (!window.zE) {
                if (!hide) {
                    this.loadScript(id);
                    this.zendeskVisible = true;
                }
                return;
            }

            if (hide && this.zendeskVisible) {
                window.zE?.hide?.();
                this.zendeskVisible = false;
            } else if (!hide && !this.zendeskVisible) {
                window.zE?.show?.();
                this.zendeskVisible = true;
            }
        }
    }

    private loadScript(id: string): void {
        window.zESettings = {
            webWidget: {
                color: {
                    theme: NZendesk.THEME,
                    launcher: NZendesk.THEME,
                    launcherText: '#FFFFFF',
                    button: NZendesk.THEME,
                    resultLists: NZendesk.THEME,
                    header: NZendesk.THEME,
                    articleLinks: NZendesk.THEME
                }
            }
        };
        this.requestService.loadScript(`https://static.zdassets.com/ekr/snippet.js?key=${id}`, {
            onScript: script => {
                script.id = 'ze-snippet';
            }
        });
    }

    private getPageMetaModel() {
        let model = this.meta?.model;

        if (model?.type === LocalPage.type) {
            return model;
        }

        return undefined;
    }
}
